import {
  arrayOf,
  bool,
  elementType,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { createContext, forwardRef, useMemo, useRef, useState } from "react";

import useResizeObserver2 from "Utilities/Hooks/useResizeObserver2";
import useScrollTo from "Utilities/Hooks/useScrollTo";

import MotionContainer from "./MotionContainer";

const FnrSlidesContext = createContext();

const FnrSlides = forwardRef(
  /**
   * @param {object} param0
   * @param {[FnrSlidesComponent]} param0.components
   * @param {string|number} param0.activeKey
   * @param {boolean} param0.fullWidth
   * @param {boolean} param0.disableScrollTo - skip the scroll-to-top the slides run on every change. For a consumer whose slides are taller than the viewport that scroll fights the page's own rules.
   * @param {boolean} param0.hideHeightMotion
   * @param {boolean} param0.fillHeight - stretches the slides container and its children to fill the parent height. Implies hideHeightMotion.
   * @param {boolean} param0.clip
   * @param {"standard"|"subtle"|"zoom"|"fade"|"swipe"|"page"} param0.variant
   * @param {"x"|"y"} param0.axis
   * @param {boolean} param0.sizeSpring - adds framer motion's type: 'spring' to animation of the size of the container. NOTE: the underlying FnrMovingDiv mutes its transition (`duration: 0`) between animations via `muted={!isAnimating}`. On every entrance after the first, the ResizeObserver fires before `onAnimationStart` propagates, and the size snaps instead of animating. If you need reliable size animation across repeated toggles, do not rely on FnrSlides' height — see fnreact/docs/VISUAL_WORKFLOW.md.
   * @param {boolean} param0.contentSpring - adds framer motion's type: 'spring' to content's transition animation
   * @param {object} param0.sizeTransition - replaces the underlying FnrMovingDiv's size transition spec entirely. Use to sync the size morph to another animation that must land at the same time with the same shape (e.g. a HighlightPill that overlays the morphing content).
   * @param {import("react").Ref} passedInRef
   * @param {boolean} fullWidthForComponent - use this to pass fullWidth down to the component
   * @returns
   */
  (
    {
      components,
      activeKey = 0,
      fullWidth,
      disableScrollTo,
      hideHeightMotion,
      fillHeight,
      clip,
      variant = "standard",
      axis = "x",
      sizeSpring,
      sizeTransition,
      contentSpring,
      fullWidthForComponent,
      ...rest
    },
    passedInRef
  ) => {
    const localRef = useRef(null);
    const ref = passedInRef || localRef;
    // Lazy on purpose: a plain argument re-runs the clientWidth read every
    // render, and that read forces layout.
    const [currentWidth, setCurrentWidth] = useState(
      () => ref.current?.clientWidth || 0
    );

    // clientWidth and not the entry's contentRect — the minWidth floor in
    // hooks.js was calibrated against the padding box. The observer fires
    // after layout, so the read forces nothing.
    useResizeObserver2(ref, () => {
      setCurrentWidth(ref.current?.clientWidth || 0);
    });

    // if in a scrollable container, go to the top of it when changing slides
    useScrollTo({
      scrollElementRef: ref,
      scrollTrigger: activeKey,
      actOnParent: true,
      disabled: disableScrollTo,
    });

    const componentProps = useMemo(() => {
      return { ...rest, fullWidth: fullWidthForComponent };
    }, [fullWidthForComponent, rest]);

    return (
      <FnrSlidesContext.Provider
        value={{
          components,
          activeKey,
          fullWidth,
          variant,
          sizeSpring,
          sizeTransition,
          contentSpring,
          componentProps,
          hideHeightMotion: hideHeightMotion || fillHeight,
          fillHeight,
          axis,
          containerRef: ref,
          currentWidth,
        }}
      >
        <MotionContainer ref={ref} clip={clip} />
      </FnrSlidesContext.Provider>
    );
  }
);

const fnrSlidesVariants = {
  standard: "standard",
  subtle: "subtle",
  zoom: "zoom",
  fade: "fade",
  swipe: "swipe",
  page: "page",
};

FnrSlides.propTypes = {
  components: arrayOf(
    shape({
      key: string.isRequired,
      value: elementType.isRequired,
      keepInDOM: bool,
    })
  ).isRequired,
  activeKey: oneOfType([string, number]),
  fullWidth: bool,
  disableScrollTo: bool,
  clip: bool,
  variant: oneOf(["standard", "subtle", "zoom", "fade", "swipe", "page"]),
  sizeSpring: bool,
  sizeTransition: object,
  contentSpring: bool,
  hideHeightMotion: bool,
  axis: oneOf(["x", "y"]),
  fillHeight: bool,
  fullWidthForComponent: bool,
};

export { FnrSlidesContext, fnrSlidesVariants };
export default FnrSlides;
